import { useState, type FormEvent } from 'react';
import { Button, Dialog, Input, Label, Select } from '../components/ui';
import { useCrews } from '../lib/queries';
import {
    useClearFlatworkSchedule,
    useCompleteFlatwork,
    useReopenFlatwork,
    useSetFlatworkSchedule,
} from '../lib/flatworkSchedule';
import { formatDateCompact, toInputDate } from '../lib/utils';
import { FLATWORK_TYPE_LABELS } from '../lib/database.types';
import type { FlatworkEvent, FlatworkType } from '../lib/database.types';

const FLATWORK_TYPES: FlatworkType[] = ['driveway', 'walkway', 'apron', 'city_sidewalk'];

function todayInput(): string {
    return new Date().toISOString().slice(0, 10);
}

interface FlatworkScheduleDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    jobLabel: string;
    current: FlatworkEvent[];
}

export function FlatworkScheduleDialog({
    open,
    onClose,
    jobId,
    jobLabel,
    current,
}: FlatworkScheduleDialogProps) {
    const { data: crews = [] } = useCrews();
    const setMut = useSetFlatworkSchedule();
    const clearMut = useClearFlatworkSchedule();
    const completeMut = useCompleteFlatwork();
    const reopenMut = useReopenFlatwork();

    // The lot's existing rows share one date and crew; types are the set.
    const [types, setTypes] = useState<Set<FlatworkType>>(
        () => new Set(current.map((c) => c.event_type))
    );
    const [scheduledDate, setScheduledDate] = useState(
        toInputDate(current[0]?.scheduled_date ?? null)
    );
    const [crewId, setCrewId] = useState(current[0]?.crew_id ?? '');
    const [completionDate, setCompletionDate] = useState(
        toInputDate(current[0]?.actual_completion_date ?? null) || todayInput()
    );
    const [error, setError] = useState<string | null>(null);

    const hasExisting = current.length > 0;
    const isComplete = hasExisting && current.every((c) => c.status === 'complete');

    function toggleType(t: FlatworkType) {
        setTypes((prev) => {
            const next = new Set(prev);
            if (next.has(t)) next.delete(t);
            else next.add(t);
            return next;
        });
    }

    async function handleSave(e: FormEvent) {
        e.preventDefault();
        setError(null);
        if (types.size === 0) {
            setError('Pick at least one flatwork type.');
            return;
        }
        if (!scheduledDate) {
            setError('Pick a scheduled date.');
            return;
        }
        try {
            await setMut.mutateAsync({
                p_job_id: jobId,
                p_event_types: FLATWORK_TYPES.filter((t) => types.has(t)),
                p_scheduled_date: scheduledDate,
                p_crew_id: crewId || null,
            });
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Save failed');
        }
    }

    async function handleClear() {
        setError(null);
        try {
            await clearMut.mutateAsync(jobId);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Clear failed');
        }
    }

    async function handleComplete() {
        setError(null);
        if (types.size === 0) {
            setError('Pick at least one flatwork type.');
            return;
        }
        if (!scheduledDate) {
            setError('Pick a scheduled date first.');
            return;
        }
        if (!completionDate) {
            setError('Pick a completion date.');
            return;
        }
        try {
            // If nothing is saved yet (or the selection changed), save the schedule
            // first so there are rows to complete. This removes the save-then-reopen
            // step Andrea hit.
            await setMut.mutateAsync({
                p_job_id: jobId,
                p_event_types: FLATWORK_TYPES.filter((t) => types.has(t)),
                p_scheduled_date: scheduledDate,
                p_crew_id: crewId || null,
            });
            await completeMut.mutateAsync({ p_job_id: jobId, p_completion_date: completionDate });
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Complete failed');
        }
    }

    async function handleReopen() {
        setError(null);
        try {
            await reopenMut.mutateAsync(jobId);
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Reopen failed');
        }
    }

    const busy =
        setMut.isPending || clearMut.isPending || completeMut.isPending || reopenMut.isPending;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Flatwork schedule"
            subtitle={jobLabel}
            footer={
                <>
                    {hasExisting ? (
                        <Button variant="danger" onClick={handleClear} disabled={busy} className="mr-auto">
                            {clearMut.isPending ? 'Clearing...' : 'Clear'}
                        </Button>
                    ) : null}
                    <Button variant="ghost" onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={types.size === 0 || !scheduledDate || busy}
                        type="submit"
                        form="flatwork-form"
                    >
                        {setMut.isPending ? 'Saving...' : 'Save'}
                    </Button>
                </>
            }
        >
            <form id="flatwork-form" onSubmit={handleSave} className="space-y-4">
                <div>
                    <Label>Types</Label>
                    <div className="grid grid-cols-2 gap-2">
                        {FLATWORK_TYPES.map((t) => (
                            <label
                                key={t}
                                className="flex items-center gap-2 px-2.5 py-2 border border-graphite-300 dark:border-graphite-700 rounded cursor-pointer select-none hover:bg-graphite-100 dark:hover:bg-graphite-900"
                            >
                                <input
                                    type="checkbox"
                                    checked={types.has(t)}
                                    onChange={() => toggleType(t)}
                                    className="accent-signal-500"
                                />
                                <span className="text-sm text-graphite-800 dark:text-graphite-200">
                                    {FLATWORK_TYPE_LABELS[t]}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label htmlFor="flatwork-date">Scheduled date</Label>
                        <Input
                            id="flatwork-date"
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                        />
                    </div>
                    <div>
                        <Label htmlFor="flatwork-crew">Crew</Label>
                        <Select
                            id="flatwork-crew"
                            value={crewId}
                            onChange={(e) => setCrewId(e.target.value)}
                        >
                            <option value="">— none —</option>
                            {crews.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                </div>

                {error ? (
                    <div className="border-l-2 border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-amber-600 dark:text-amber-400">
                        {error}
                    </div>
                ) : null}
            </form>

            {/* Completion block — available as soon as a schedule is entered.
                If nothing is saved yet, Mark complete saves first, then completes,
                so there's no save-then-reopen step. */}
            {isComplete || types.size > 0 ? (
                <div className="mt-4 pt-4 border-t border-graphite-200 dark:border-graphite-800">
                    {isComplete ? (
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex flex-col">
                                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-done-600 dark:text-done-500">
                                    Flatwork complete
                                </span>
                                <span className="font-mono text-xs text-graphite-700 dark:text-graphite-300 tabular">
                                    {current[0]?.actual_completion_date
                                        ? formatDateCompact(current[0].actual_completion_date)
                                        : '—'}
                                </span>
                            </div>
                            <Button variant="secondary" onClick={handleReopen} disabled={busy}>
                                {reopenMut.isPending ? 'Reopening...' : 'Reopen'}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-end justify-between gap-3">
                            <div className="flex-1 max-w-[180px]">
                                <Label htmlFor="flatwork-complete-date">Completion date</Label>
                                <Input
                                    id="flatwork-complete-date"
                                    type="date"
                                    value={completionDate}
                                    onChange={(e) => setCompletionDate(e.target.value)}
                                />
                            </div>
                            <Button
                                variant="primary"
                                onClick={handleComplete}
                                disabled={types.size === 0 || !scheduledDate || !completionDate || busy}
                            >
                                {completeMut.isPending ? 'Marking...' : 'Mark complete'}
                            </Button>
                        </div>
                    )}
                </div>
            ) : null}
        </Dialog>
    );
}
